import React from 'react';

import useModal from '../hooks/useModal';
import {
  UpdateAccess,
  UpdateInsurance,
  UpdateLanguage,
  UpdateName,
  UpdatePatientVerification,
  UpdateServices,
  UpdateSpecializations,
  UpdateWorkingHours,
  WeeklyReports
} from '@/components/dashboard';

interface ProfileModalsProps {
  modals: {
    name: { isOpen: boolean; onOpen: () => void; onClose: () => void };
    weeklyReports: { isOpen: boolean; onOpen: () => void; onClose: () => void };
    workingHours: { isOpen: boolean; onOpen: () => void; onClose: () => void };
    services: { isOpen: boolean; onOpen: () => void; onClose: () => void };
    insurance: { isOpen: boolean; onOpen: () => void; onClose: () => void };
    access: { isOpen: boolean; onOpen: () => void; onClose: () => void };
    patientVerification: {
      isOpen: boolean;
      onOpen: () => void;
      onClose: () => void;
    };
    specializations: {
      isOpen: boolean;
      onOpen: () => void;
      onClose: () => void;
    };
    language: { isOpen: boolean; onOpen: () => void; onClose: () => void };
  };
}

/**
 * Higher-Order Component that provides all profile-related modals with their state management.
 *
 * This HOC wraps a component and provides:
 * - Context values (isClinic, dentistId)
 * - Modal state and controls as props
 * - Renders all modal components
 *
 * @param WrappedComponent - The component to wrap with modal functionality
 * @returns Enhanced component with modal props and context values
 */
const withProfileModals = <P extends ProfileModalsProps>(
  WrappedComponent: React.ComponentType<P>
) => {
  const ComponentWithProfileModals = (
    props: Omit<P, keyof ProfileModalsProps>
  ) => {
    // Modal states
    const nameModal = useModal();
    const weeklyReportsModal = useModal();
    const workingHoursModal = useModal();
    const servicesModal = useModal();
    const insuranceModal = useModal();
    const accessModal = useModal();
    const patientVerificationModal = useModal();
    const specializationsModal = useModal();
    const languageModal = useModal();

    // Modal controls object
    const modals = {
      name: {
        isOpen: nameModal.isOpen,
        onOpen: nameModal.onOpen,
        onClose: nameModal.onClose
      },
      weeklyReports: {
        isOpen: weeklyReportsModal.isOpen,
        onOpen: weeklyReportsModal.onOpen,
        onClose: weeklyReportsModal.onClose
      },
      workingHours: {
        isOpen: workingHoursModal.isOpen,
        onOpen: workingHoursModal.onOpen,
        onClose: workingHoursModal.onClose
      },
      services: {
        isOpen: servicesModal.isOpen,
        onOpen: servicesModal.onOpen,
        onClose: servicesModal.onClose
      },
      insurance: {
        isOpen: insuranceModal.isOpen,
        onOpen: insuranceModal.onOpen,
        onClose: insuranceModal.onClose
      },
      access: {
        isOpen: accessModal.isOpen,
        onOpen: accessModal.onOpen,
        onClose: accessModal.onClose
      },
      patientVerification: {
        isOpen: patientVerificationModal.isOpen,
        onOpen: patientVerificationModal.onOpen,
        onClose: patientVerificationModal.onClose
      },
      specializations: {
        isOpen: specializationsModal.isOpen,
        onOpen: specializationsModal.onOpen,
        onClose: specializationsModal.onClose
      },
      language: {
        isOpen: languageModal.isOpen,
        onOpen: languageModal.onOpen,
        onClose: languageModal.onClose
      }
    };

    return (
      <>
        <WrappedComponent {...(props as P)} modals={modals} />

        {/* Modal Components */}
        <UpdateName
          isOpen={nameModal.isOpen}
          onClose={nameModal.onClose}
          presentingElement={undefined}
        />
        <WeeklyReports
          isOpen={weeklyReportsModal.isOpen}
          onClose={weeklyReportsModal.onClose}
          presentingElement={undefined}
        />
        <UpdateWorkingHours
          isOpen={workingHoursModal.isOpen}
          onClose={workingHoursModal.onClose}
          presentingElement={undefined}
        />
        <UpdateServices
          isOpen={servicesModal.isOpen}
          onClose={servicesModal.onClose}
          presentingElement={undefined}
        />
        <UpdateInsurance
          isOpen={insuranceModal.isOpen}
          onClose={insuranceModal.onClose}
          presentingElement={undefined}
        />
        <UpdateAccess
          isOpen={accessModal.isOpen}
          onClose={accessModal.onClose}
          presentingElement={undefined}
        />
        <UpdatePatientVerification
          isOpen={patientVerificationModal.isOpen}
          onClose={patientVerificationModal.onClose}
          presentingElement={undefined}
        />
        <UpdateSpecializations
          isOpen={specializationsModal.isOpen}
          onClose={specializationsModal.onClose}
          presentingElement={undefined}
        />
        <UpdateLanguage
          isOpen={languageModal.isOpen}
          onClose={languageModal.onClose}
          presentingElement={undefined}
        />
      </>
    );
  };

  ComponentWithProfileModals.displayName = `withProfileModals(${WrappedComponent.displayName || WrappedComponent.name})`;

  return ComponentWithProfileModals;
};

export default withProfileModals;
